// shared helpers for SNMP evidence/finding rendering.
//
// Dorothy's normalized SNMP payload lives entirely inside
// engagement_evidence.raw_json. two practical wrinkles surface in
// production data:
//   1. raw_json is stored as JSONB but populated with a JSON STRING
//      (double-encoded), so accessing .target on it returns undefined.
//      `normalizeRawJson` parses repeatedly until it lands on an object
//      or array.
//   2. the table evidence types (snmp_interfaces / snmp_routes /
//      snmp_arp) store the row array AT THE TOP LEVEL of raw_json, not
//      under a named property.
//   3. Dorothy's column names are PascalCase ("Index", "Descr",
//      "SpeedBps", "AdminStatus", "Destination", "NextHop", "Ip",
//      "Mac"). the extractors below check both PascalCase and the
//      SNMP MIB-style ifFoo names.
//
// keep extraction in one module so every view (drawer cards, Evidence
// tab summary cell, Host drawer SNMP block) reads the same fields

import type { EngagementEvidence, EngagementFinding } from '@/lib/engagementsApi'

// ── Evidence-type taxonomy + labels ────────────────────────────────────

export const SNMP_EVIDENCE_TYPES = new Set<string>([
  'snmp_system',
  'snmp_default_community',
  'snmp_sensitive_community',
  'snmp_interfaces',
  'snmp_routes',
  'snmp_arp',
  'snmp_neighbors',
])

export function humanEvidenceType(type: string | null | undefined): string {
  if (!type) return '—'
  switch (type) {
    case 'snmp_system':              return 'SNMP system info'
    case 'snmp_default_community':   return 'SNMP default community'
    case 'snmp_sensitive_community': return 'SNMP sensitive community'
    case 'snmp_interfaces':          return 'SNMP interfaces'
    case 'snmp_routes':              return 'SNMP routes'
    case 'snmp_arp':                 return 'SNMP ARP table'
    case 'snmp_neighbors':           return 'SNMP neighbours'
    case 'service_banner':           return 'Service banner'
    case 'service_inventory':        return 'Service inventory'
    case 'attack_run':               return 'Attack simulation'
    default:                          return type.replace(/_/g, ' ')
  }
}

export function isSnmpEvidence(ev: { evidence_type?: string | null }): boolean {
  return !!ev.evidence_type && SNMP_EVIDENCE_TYPES.has(ev.evidence_type)
}

export function isSnmpFinding(
  finding: { category?: string | null; title?: string | null; evidence_ids?: string[] | null },
  evidence: EngagementEvidence[],
): boolean {
  if ((finding.category ?? '').toLowerCase().includes('snmp')) return true
  if ((finding.title ?? '').toLowerCase().includes('snmp')) return true
  if (Array.isArray(finding.evidence_ids)) {
    const idSet = new Set(finding.evidence_ids)
    for (const ev of evidence) {
      if (idSet.has(ev.id) && isSnmpEvidence(ev)) return true
    }
  }
  return false
}

// ── normalizeRawJson ───────────────────────────────────────────────────
// returns an object/array regardless of what Dorothy actually shipped.
// handles:
//   - null / undefined / empty string  → {}
//   - already-object                    → returns as-is
//   - already-array                     → returns as-is
//   - JSON string                       → JSON.parse once
//   - double-encoded JSON string        → JSON.parse repeatedly (max 3)
//   - nested wrappers (raw_json.raw_json / .data / .result / .evidence)
//
// never throws. logs nothing, bad shapes silently degrade to {}

export function normalizeRawJson(input: unknown): any {
  let cur: any = input
  // drop nulls / undefined / empty
  if (cur == null) return {}
  if (typeof cur === 'string' && cur.trim() === '') return {}

  // parse repeatedly while the value is still a string. SAFETY: cap
  // iterations so a pathological input ("\"\"\"\"...") can't loop
  for (let i = 0; i < 4; i++) {
    if (typeof cur !== 'string') break
    try {
      cur = JSON.parse(cur)
    } catch {
      // unparseable string, return empty object so callers can keep
      // rendering. the original is still on the evidence row for the
      // raw-JSON viewer
      return {}
    }
  }

  if (cur == null) return {}
  if (typeof cur !== 'object') {
    // string/number/bool after parsing, wrap so destructuring is safe
    return { value: cur }
  }

  // unwrap a single-level nest if Dorothy put the real payload inside a
  // wrapper. only descend when the wrapper itself has nothing else
  // useful at the top level
  for (const key of ['raw_json', 'data', 'result', 'evidence']) {
    if (Array.isArray(cur)) break
    const inner = cur[key]
    if (!inner) continue
    const ownKeys = Object.keys(cur).filter(k => k !== key)
    if (ownKeys.length === 0) {
      cur = inner
      // re-parse if the inner is still a string
      if (typeof cur === 'string') {
        try { cur = JSON.parse(cur) } catch { /* leave string-wrapped */ }
      }
    }
  }

  return cur
}

// returns the parsed raw_json AND the evidence type so the
// array-vs-object decision below can use the type when raw_json is
// itself an array
function normWithType(ev: EngagementEvidence): { raw: any; type: string | null | undefined } {
  return { raw: normalizeRawJson(ev.raw_json), type: ev.evidence_type }
}

// ── Field extraction ───────────────────────────────────────────────────

export interface SnmpAuditSummary {
  target?:                     string | null
  target_ip?:                  string | null
  target_port?:                number | null
  version?:                    string | null
  audit_mode?:                 string | null
  dictionary_source?:          string | null
  dictionary_size?:            number | null
  tested_communities?:         number | string[] | null
  valid_communities?:          string[] | null
  enumeration_community?:      string | null
  enumeration_reason?:         string | null
  access_classification?:      string | null
  no_destructive_probe?:       boolean | null
  community?:                  string | null
  community_role?:             string | null
}

// IPv4 regex shared by host-fallback extractors. tightened to four
// 1-3 digit octets so it doesn't grab "192.168" out of free text
const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/

// "192.168.0.24:161" → { ip: "192.168.0.24", port: 161 }
function parseEndpoint(s: string | null | undefined): { ip: string | null; port: number | null } {
  if (typeof s !== 'string') return { ip: null, port: null }
  const m = s.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(?::(\d+))?$/)
  if (!m) {
    const ipMatch = s.match(IPV4_RE)
    return { ip: ipMatch ? ipMatch[0] : null, port: null }
  }
  return { ip: m[1], port: m[2] ? parseInt(m[2], 10) : null }
}

export function extractAuditSummary(rawIn: any): SnmpAuditSummary {
  const raw = normalizeRawJson(rawIn)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const tested = raw.tested_communities ?? raw.testedCommunities
  // Dorothy ships `target` without a port and `port` separately, but
  // older payloads embedded the port inline. handle both
  const targetRaw    = raw.target ?? raw.target_addr ?? raw.endpoint ?? null
  const endpoint     = parseEndpoint(targetRaw)
  const explicitPort = raw.target_port ?? raw.targetPort ?? raw.port ?? null
  return {
    target:                  targetRaw && endpoint.port == null && explicitPort
      ? `${targetRaw}:${explicitPort}` : targetRaw,
    target_ip:               raw.target_ip ?? raw.targetIp ?? raw.host_ip ?? raw.hostIp ?? raw.host ?? endpoint.ip ?? null,
    target_port:             explicitPort ?? endpoint.port ?? null,
    version:                 raw.snmp_version ?? raw.snmpVersion ?? raw.version ?? null,
    audit_mode:              raw.audit_mode ?? raw.auditMode ?? null,
    dictionary_source:       raw.dictionary_source ?? raw.dictionarySource ?? null,
    dictionary_size:         typeof raw.dictionary_size === 'number' ? raw.dictionary_size
                              : typeof raw.dictionarySize === 'number' ? raw.dictionarySize
                              : null,
    tested_communities:      Array.isArray(tested) ? tested
                              : typeof tested === 'number' ? tested
                              : null,
    valid_communities:       Array.isArray(raw.valid_communities) ? raw.valid_communities
                              : Array.isArray(raw.validCommunities) ? raw.validCommunities
                              : null,
    enumeration_community:   raw.enumeration_community ?? raw.enumerationCommunity ?? raw.selected_community ?? raw.selectedCommunity ?? null,
    enumeration_reason:      raw.enumeration_reason ?? raw.enumerationReason ?? null,
    access_classification:   raw.access_classification ?? raw.accessClassification ?? raw.access ?? raw.access_level ?? null,
    no_destructive_probe:    typeof raw.no_destructive_probe_performed === 'boolean' ? raw.no_destructive_probe_performed
                              : typeof raw.noDestructiveProbePerformed === 'boolean' ? raw.noDestructiveProbePerformed
                              // Dorothy sometimes encodes the same fact inline as
                              // "no destructive probe performed" in the access string
                              : (typeof (raw.access ?? raw.access_level) === 'string'
                                 && /no destructive (write )?probe/i.test(raw.access ?? raw.access_level))
                                  ? true : null,
    community:               raw.community ?? raw.valid_community ?? null,
    community_role:          raw.community_role ?? raw.communityRole ?? raw.role ?? null,
  }
}

export interface SnmpSystemInfo {
  sysName?:     string | null
  sysDescr?:    string | null
  sysObjectID?: string | null
  sysLocation?: string | null
  sysContact?:  string | null
  uptime?:      string | number | null
  vendor?:      string | null
  model?:       string | null
  deviceType?:  string | null
}

export function extractSystemInfo(rawIn: any): SnmpSystemInfo {
  const raw = normalizeRawJson(rawIn)
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const src = raw.system ?? raw.system_info ?? raw.systemInfo ?? raw
  // treat sys_descr=" " (a single space, Dorothy's null-ish) as empty
  const clean = (v: any) => (typeof v === 'string' && v.trim() === '' ? null : v)
  return {
    sysName:     clean(src.sysName     ?? src.sys_name     ?? null),
    sysDescr:    clean(src.sysDescr    ?? src.sys_descr    ?? src.sysDescription ?? null),
    sysObjectID: clean(src.sysObjectID ?? src.sys_object_id ?? src.sysObjectId    ?? null),
    sysLocation: clean(src.sysLocation ?? src.sys_location ?? null),
    sysContact:  clean(src.sysContact  ?? src.sys_contact  ?? null),
    uptime:      src.uptime ?? src.uptime_seconds ?? src.uptimeSeconds ?? src.sysUpTime ?? null,
    vendor:      clean(src.vendor      ?? raw.vendor       ?? null),
    model:       clean(src.model       ?? raw.model        ?? null),
    deviceType:  clean(src.device_type ?? src.deviceType ?? raw.device_type ?? raw.deviceType ?? null),
  }
}

export interface SnmpInterfaceRow {
  index?:         number | string | null
  name?:          string | null
  description?:   string | null
  mac?:           string | null
  speed?:         number | string | null
  admin_status?:  string | null
  oper_status?:   string | null
  ip?:            string | null
}

// map Dorothy admin/oper status integers to readable strings (RFC 2863)
function ifStatus(v: any): string | null {
  if (v == null) return null
  if (typeof v === 'string') return v
  switch (v) {
    case 1: return 'up'
    case 2: return 'down'
    case 3: return 'testing'
    case 4: return 'unknown'
    case 5: return 'dormant'
    case 6: return 'notPresent'
    case 7: return 'lowerLayerDown'
    default: return String(v)
  }
}

// resolve the array source for a typed table evidence. order:
//   1. top-level array (Dorothy ships snmp_interfaces as the array itself)
//   2. named child key matching the evidence type
//   3. common wrapper keys (tables.X, snmp.X, result.X, data.X, records)
function pickTableArray(rawIn: any, type: string | null | undefined, keys: string[]): any[] {
  const raw = normalizeRawJson(rawIn)
  if (Array.isArray(raw)) return raw
  if (!raw || typeof raw !== 'object') return []
  for (const k of keys) {
    if (Array.isArray(raw[k])) return raw[k]
  }
  // nested under common wrappers
  for (const wrap of ['tables', 'snmp', 'result', 'data']) {
    const sub = raw[wrap]
    if (!sub || typeof sub !== 'object') continue
    for (const k of keys) {
      if (Array.isArray(sub[k])) return sub[k]
    }
  }
  if (Array.isArray(raw.records)) {
    // `records` is the generic dump array Dorothy uses when it doesn't
    // know which type it is. only trust it when the evidence_type says
    // this is a table evidence
    if (type === 'snmp_interfaces' || type === 'snmp_routes' || type === 'snmp_arp' || type === 'snmp_neighbors') return raw.records
  }
  return []
}

export function extractInterfaces(rawIn: any, type?: string | null): SnmpInterfaceRow[] {
  const arr = pickTableArray(rawIn, type, ['interfaces', 'ifTable', 'if_table', 'interfaceTable'])
  return arr.map((r: any) => ({
    index:        r.Index ?? r.ifIndex ?? r.index ?? r.idx ?? null,
    name:         r.Name  ?? r.ifName  ?? r.name  ?? null,
    description:  r.Descr ?? r.ifDescr ?? r.description ?? r.descr ?? null,
    mac:          r.PhysAddress ?? r.physAddress ?? r.ifPhysAddress ?? r.mac ?? r.macAddress ?? r.MacAddress ?? null,
    speed:        r.HighSpeedMbps != null ? `${r.HighSpeedMbps} Mbps`
                : r.SpeedBps      != null ? `${Math.round(r.SpeedBps / 1_000_000)} Mbps`
                : r.ifSpeed       ?? r.speed ?? r.speedBps ?? null,
    admin_status: ifStatus(r.AdminStatus ?? r.ifAdminStatus ?? r.admin_status ?? r.adminStatus ?? null),
    oper_status:  ifStatus(r.OperStatus  ?? r.ifOperStatus  ?? r.oper_status  ?? r.operStatus  ?? null),
    ip:           r.Ip ?? r.ifIp ?? r.ip ?? r.ipAddress ?? r.address ?? null,
  }))
}

export interface SnmpRouteRow {
  destination?: string | null
  prefix?:      string | null
  next_hop?:    string | null
  interface?:   string | null
  type?:        string | null
}

export function extractRoutes(rawIn: any, type?: string | null): SnmpRouteRow[] {
  const arr = pickTableArray(rawIn, type, ['routes', 'routeTable', 'route_table', 'ipRouteTable'])
  return arr.map((r: any) => {
    const prefix = r.PrefixLen != null ? `/${r.PrefixLen}`
                 : r.prefixLength != null ? `/${r.prefixLength}`
                 : r.prefix ?? r.mask ?? r.ipRouteMask ?? null
    return {
      destination: r.Destination ?? r.destination ?? r.dest ?? r.routeDest ?? r.ipRouteDest ?? null,
      prefix,
      next_hop:    r.NextHop ?? r.next_hop ?? r.nexthop ?? r.gateway ?? r.ipRouteNextHop ?? null,
      interface:   (r.IfIndex ?? r.ifIndex ?? r.interface ?? r.iface ?? r.ifName ?? r.ipRouteIfIndex)?.toString() ?? null,
      type:        r.Type ?? r.type ?? r.source ?? r.routeType ?? r.ipRouteProto ?? null,
    }
  })
}

export interface SnmpArpRow {
  ip?:        string | null
  mac?:       string | null
  interface?: string | null
  type?:      string | null
}

export function extractArp(rawIn: any, type?: string | null): SnmpArpRow[] {
  const arr = pickTableArray(rawIn, type, ['arp', 'arpTable', 'arp_table', 'ip_net_to_media', 'ipNetToMediaTable'])
  return arr.map((r: any) => ({
    ip:        r.Ip ?? r.ip ?? r.address ?? r.ipAddress ?? r.ipNetToMediaNetAddress ?? null,
    mac:       r.Mac ?? r.mac ?? r.physAddress ?? r.macAddress ?? r.ipNetToMediaPhysAddress ?? null,
    interface: (r.IfIndex ?? r.ifIndex ?? r.interface ?? r.iface ?? r.ifName ?? r.ipNetToMediaIfIndex)?.toString() ?? null,
    type:      r.Type ?? r.type ?? r.state ?? r.source ?? null,
  }))
}

export function extractNeighbors(rawIn: any, type?: string | null): SnmpArpRow[] {
  const arr = pickTableArray(rawIn, type, ['neighbors', 'neighbours'])
  return arr.map((r: any) => ({
    ip:        r.Ip ?? r.ip ?? r.address ?? null,
    mac:       r.Mac ?? r.mac ?? r.physAddress ?? null,
    interface: (r.IfIndex ?? r.ifIndex ?? r.interface ?? r.ifName)?.toString() ?? null,
    type:      r.Type ?? r.type ?? r.state ?? null,
  }))
}

export interface SnmpOpenPort {
  port:     number
  protocol: string
  service?: string | null
  banner?:  string | null
}

export function extractOpenPorts(rawIn: any): SnmpOpenPort[] {
  const raw = normalizeRawJson(rawIn)
  if (!raw || typeof raw !== 'object') return []
  const arr = raw.open_ports ?? raw.openPorts ?? null
  if (!Array.isArray(arr)) return []
  return arr.map((p: any) => ({
    port:     p.port ?? 0,
    protocol: p.protocol ?? 'tcp',
    service:  p.service ?? null,
    banner:   p.banner ?? null,
  }))
}

// ── Host-IP fallback chain ─────────────────────────────────────────────
// Dorothy doesn't always populate engagement_evidence.asset_id or
// .external_asset_id, so the Evidence tab / Host drawer fall back to
// these progressive guesses

export function evidenceHostIp(ev: EngagementEvidence): string | null {
  if (ev.asset?.host_ip)   return ev.asset.host_ip
  const e: any = ev
  if (typeof e.host_ip === 'string')   return e.host_ip
  if (typeof e.hostIp === 'string')    return e.hostIp
  if (typeof e.target_ip === 'string') return e.target_ip
  if (typeof e.targetIp === 'string')  return e.targetIp
  const raw = normalizeRawJson(ev.raw_json)
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    if (typeof raw.target_ip === 'string') return raw.target_ip
    if (typeof raw.targetIp === 'string')  return raw.targetIp
    if (typeof raw.host_ip === 'string')   return raw.host_ip
    if (typeof raw.hostIp === 'string')    return raw.hostIp
    if (typeof raw.target === 'string') {
      const ep = parseEndpoint(raw.target)
      if (ep.ip) return ep.ip
    }
    if (typeof raw.endpoint === 'string') {
      const ep = parseEndpoint(raw.endpoint)
      if (ep.ip) return ep.ip
    }
  }
  for (const text of [ev.summary, (e as any).title]) {
    if (typeof text === 'string') {
      const m = text.match(IPV4_RE)
      if (m) return m[0]
    }
  }
  return null
}

export function findingHostIp(finding: EngagementFinding, evidence: EngagementEvidence[]): string | null {
  if (finding.asset?.host_ip) return finding.asset.host_ip
  const f: any = finding
  if (typeof f.host_ip === 'string')        return f.host_ip
  if (typeof f.affected_host === 'string')  return f.affected_host
  if (typeof f.affectedHost === 'string')   return f.affectedHost
  const raw = normalizeRawJson(finding.raw_json)
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    if (typeof raw.target_ip === 'string') return raw.target_ip
    if (typeof raw.host_ip === 'string')   return raw.host_ip
    if (typeof raw.target === 'string') {
      const ep = parseEndpoint(raw.target)
      if (ep.ip) return ep.ip
    }
  }
  for (const text of [finding.title, finding.recommendation, finding.category]) {
    if (typeof text === 'string') {
      const m = text.match(IPV4_RE)
      if (m) return m[0]
    }
  }
  // last resort: pull from any linked evidence
  const linked = resolveLinkedEvidence(finding, evidence)
  for (const ev of linked) {
    const ip = evidenceHostIp(ev)
    if (ip) return ip
  }
  return null
}

// ── Summaries for tables / collapsed list cells ─────────────────────────

export function snmpEvidenceKeySummary(ev: EngagementEvidence): string {
  if (!isSnmpEvidence(ev)) return ev.summary ?? ''
  const { raw, type } = normWithType(ev)
  const a = extractAuditSummary(raw)
  const sys = extractSystemInfo(raw)
  const parts: string[] = []
  const community = displayCommunity(a)
  if (community && (type === 'snmp_sensitive_community' || type === 'snmp_default_community')) {
    parts.push(`community: ${community}`)
  } else if (Array.isArray(a.valid_communities) && a.valid_communities.length > 0) {
    parts.push(`valid: ${a.valid_communities.join(', ')}`)
  }
  if (a.enumeration_community && a.enumeration_community !== community) {
    parts.push(`enum: ${a.enumeration_community}`)
  }
  if (sys.sysName) parts.push(`sysName: ${sys.sysName}`)
  const ifs = extractInterfaces(raw, type).length
  const rts = extractRoutes(raw, type).length
  const arp = extractArp(raw, type).length
  const nbr = extractNeighbors(raw, type).length
  if (ifs > 0) parts.push(`ifs: ${ifs}`)
  if (rts > 0) parts.push(`routes: ${rts}`)
  if (arp > 0) parts.push(`arp: ${arp}`)
  if (nbr > 0) parts.push(`neighbours: ${nbr}`)
  if (parts.length === 0 && ev.summary) return ev.summary
  return parts.length > 0 ? parts.join(' · ') : (ev.summary ?? '')
}

// ── Community helpers ──────────────────────────────────────────────────

// generic words that are NOT real community names. they appear in
// Dorothy fields only when extraction picked up a label instead of a
// value. use this set to suppress fallback display
const GENERIC_COMMUNITY_NAMES = new Set([
  'community', 'default', 'snmp', 'read', 'readonly', '', '<unknown>',
])

function isMeaningfulCommunity(c: string | null | undefined): c is string {
  if (typeof c !== 'string') return false
  if (!c.trim()) return false
  return !GENERIC_COMMUNITY_NAMES.has(c.toLowerCase())
}

// pick the best community name to display from an audit summary.
// priority: enumeration_community → meaningful single .community →
// highest-risk valid_community → first valid_community → null
export function displayCommunity(a: SnmpAuditSummary): string | null {
  if (isMeaningfulCommunity(a.enumeration_community)) return a.enumeration_community
  if (isMeaningfulCommunity(a.community))             return a.community
  if (Array.isArray(a.valid_communities)) {
    // highest-risk first
    const sorted = [...a.valid_communities].filter(isMeaningfulCommunity).sort((x, y) => {
      const ranks: Record<'critical' | 'high' | 'medium', number> = { critical: 0, high: 1, medium: 2 }
      return ranks[snmpCommunityRisk(x)] - ranks[snmpCommunityRisk(y)]
    })
    if (sorted[0]) return sorted[0]
  }
  return null
}

// "private"/"admin"/"write" → critical, "public"/"default" → high
export function snmpCommunityRisk(community: string | null | undefined): 'critical' | 'high' | 'medium' {
  if (!community) return 'medium'
  const c = community.toLowerCase()
  if (['private', 'admin', 'manager', 'write', 'readwrite', 'rw'].some(k => c.includes(k))) return 'critical'
  if (['public', 'default'].some(k => c.includes(k))) return 'high'
  return 'medium'
}

// ── Finding presentation ───────────────────────────────────────────────

function communityFromTextFallback(text: string | null | undefined): string | null {
  if (!text) return null
  // look for "community 'xxx'" or "detected: 'xxx'" or "detected: xxx"
  const m = text.match(/community\s+['"`]?([A-Za-z0-9_-]+)['"`]?/i)
          ?? text.match(/detected\s*:\s*['"`]?([A-Za-z0-9_-]+)['"`]?/i)
  if (m && isMeaningfulCommunity(m[1])) return m[1]
  return null
}

export function snmpFindingTitle(finding: EngagementFinding, evidence: EngagementEvidence[]): string {
  if (finding.title && !finding.title.toLowerCase().startsWith('snmp finding')) return finding.title
  const linked = resolveLinkedEvidence(finding, evidence)
  for (const ev of linked) {
    const a = extractAuditSummary(ev.raw_json)
    const c = displayCommunity(a)
    if (c) {
      const risk = snmpCommunityRisk(c)
      return risk === 'critical'
        ? `SNMP sensitive community detected: ${c}`
        : `SNMP default community detected: ${c}`
    }
  }
  return finding.title ?? 'SNMP finding'
}

export function snmpFindingSummary(finding: EngagementFinding, evidence: EngagementEvidence[]): string | null {
  const linked = resolveLinkedEvidence(finding, evidence)
  let community: string | null = null
  let targetIp: string | null = null
  let targetPort: number | null = 161
  let version: string | null = 'SNMPv2c'
  const readTables = { interfaces: false, routes: false, arp: false }
  for (const ev of linked) {
    const a = extractAuditSummary(ev.raw_json)
    if (!community) community = displayCommunity(a)
    if (!targetIp)  targetIp  = a.target_ip ?? null
    if (a.target_port) targetPort = a.target_port
    if (a.version)     version    = a.version
    if (extractInterfaces(ev.raw_json, ev.evidence_type).length > 0) readTables.interfaces = true
    if (extractRoutes(ev.raw_json, ev.evidence_type).length > 0)     readTables.routes     = true
    if (extractArp(ev.raw_json, ev.evidence_type).length > 0)        readTables.arp        = true
  }
  // Try the finding's own raw_json + title for community as a fallback.
  if (!community) community = displayCommunity(extractAuditSummary(finding.raw_json))
  if (!community) community = communityFromTextFallback(finding.title)
  if (!targetIp)  targetIp  = findingHostIp(finding, evidence)
  if (!community && !targetIp) return null
  const risk = snmpCommunityRisk(community)
  const noun = risk === 'critical' ? 'sensitive community' : 'default community'
  const communityText = community ? `'${community}'` : '(unknown)'
  // spec wording, describes what was confirmed, with the explicit
  // safeguard line so the reader knows no destructive write probe ran.
  // target is appended only when known
  const targetSuffix = targetIp ? ` on ${targetIp}:${targetPort ?? 161}` : ''
  return `SNMP ${noun} ${communityText} was accepted${targetSuffix} over ${version ?? 'SNMPv2c'}. ` +
         `Device identity and management data were readable. No destructive write probe was performed.`
}

export const SNMP_RECOMMENDATION =
  'Rotate this community string immediately; restrict SNMP to known managers; ' +
  'disable SNMPv1/v2c in favour of SNMPv3 with auth+priv.'

// resolve evidence_ids[] strings (DB ids or external Dorothy ids) back
// into the actual evidence rows. synth ids match against the same id
// field, so synth evidence resolves too
export function resolveLinkedEvidence(
  finding: { evidence_ids?: string[] | null },
  evidence: EngagementEvidence[],
): EngagementEvidence[] {
  if (!Array.isArray(finding.evidence_ids) || finding.evidence_ids.length === 0) return []
  const idSet = new Set(finding.evidence_ids)
  return evidence.filter(ev => idSet.has(ev.id))
}
